import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiRequest } from '../services/api';

const CartContext = createContext();

const CART_STORAGE_KEY = 'shabotique_cart_v1';

// Initial state loaded from localStorage if available
const getInitialCartState = () => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    console.warn("Could not load cart from localStorage", e);
    return [];
  }
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const { product, color, size, topSize, bottomSize, quantity = 1 } = action.payload;
      
      // Distinct key based on item variant
      const variantKey = `${product.id}-${color || ''}-${topSize || size || ''}-${bottomSize || ''}`;
      
      const existingIndex = state.findIndex(item => item.variantKey === variantKey);

      if (existingIndex > -1) {
        // Merge identical variant with stock check
        return state.map((item, index) => {
          if (index === existingIndex) {
            const newQty = Math.min(item.quantity + quantity, item.maxStock || 10);
            return { ...item, quantity: newQty };
          }
          return item;
        });
      } else {
        // Add new line item
        const newItem = {
          variantKey,
          productId: product.id,
          slug: product.slug,
          name: product.name,
          category: product.category,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          image: product.images && product.images.length > 0 ? product.images[0] : '',
          color: color || (product.colours && product.colours[0]?.name) || '',
          size: size || null,
          topSize: topSize || null,
          bottomSize: bottomSize || null,
          isSet: Boolean(product.isSet),
          quantity: Math.min(quantity, product.stock || 10),
          maxStock: product.stock || 10
        };
        return [...state, newItem];
      }
    }

    case 'UPDATE_QUANTITY': {
      const { variantKey, quantity } = action.payload;
      if (quantity <= 0) {
        return state.filter(item => item.variantKey !== variantKey);
      }
      return state.map(item => {
        if (item.variantKey === variantKey) {
          const enforced = Math.min(quantity, item.maxStock || 10);
          return { ...item, quantity: enforced };
        }
        return item;
      });
    }

    case 'REMOVE_ITEM': {
      return state.filter(item => item.variantKey !== action.payload.variantKey);
    }

    case 'CLEAR_CART': {
      return [];
    }

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  // Store delivery / COD / GST settings (defaults until loaded)
  const [charges, setCharges] = React.useState({ shippingFee: 99, freeShippingThreshold: 1999, codEnabled: true, codFee: 0, gstEnabled: false, gstInclusive: true });
  useEffect(() => {
    apiRequest('/store-info').then(info => info && setCharges(prev => ({ ...prev, ...info }))).catch(() => {});
  }, []);

  const [cartItems, dispatch] = useReducer(cartReducer, [], getInitialCartState);
  const [coupon, setCoupon] = React.useState(null);
  const [toastMessage, setToastMessage] = React.useState(null);

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (e) {
      console.warn("Could not sync cart to localStorage", e);
    }
  }, [cartItems]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const addToCart = (product, options) => {
    dispatch({
      type: 'ADD_TO_CART',
      payload: { product, ...options }
    });
    showToast(`Added "${product.name}" to your shopping bag`);
  };

  const updateQuantity = (variantKey, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { variantKey, quantity }
    });
  };

  const removeFromCart = (variantKey) => {
    dispatch({
      type: 'REMOVE_ITEM',
      payload: { variantKey }
    });
    showToast("Item removed from your shopping bag");
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    setCoupon(null);
  };

  // Coupons are checked by the server (code, expiry, usage limit, minimum order).
  const applyCoupon = async (code) => {
    if (!code) return { success: false, message: "Please enter a coupon code" };
    try {
      const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const result = await apiRequest('/coupons/validate', {
        method: 'POST',
        body: { code: code.trim(), subtotal }
      });
      setCoupon({ code: result.code, type: result.type, percent: result.percent, amount: result.amount, label: result.label });
      showToast(`Coupon '${result.code}' applied! ${result.label}`);
      return { success: true, message: `${result.label} applied successfully!` };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    showToast("Coupon removed");
  };

  // Calculations
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartSubtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  // Delivery charge and free-delivery amount come from Admin → Settings
  const freeFrom = charges.freeShippingThreshold;
  const shippingFee = cartSubtotal === 0 || (freeFrom > 0 && cartSubtotal >= freeFrom) ? 0 : charges.shippingFee;
  
  // Coupons are either a percentage or a flat ₹ amount (never more than the subtotal)
  const discountAmount = !coupon
    ? 0
    : coupon.type === 'flat'
      ? Math.min(coupon.amount || 0, cartSubtotal)
      : Math.round((cartSubtotal * (coupon.percent || 0)) / 100);
  const cartTotal = Math.max(0, cartSubtotal - discountAmount + shippingFee);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        cartSubtotal,
        shippingFee,
        freeShippingThreshold: charges.freeShippingThreshold,
        charges,
        discountAmount,
        cartTotal,
        coupon,
        toastMessage,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        applyCoupon,
        removeCoupon,
        showToast
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

